import axios from "axios";
import { getPublicData } from "../utils/savedata";
import tokenManager from "../utils/tokenManager";
import { getRetryAfterSeconds, handleRateLimited, isGatewayRateLimited, rateLimitMessage } from "../utils/rateLimit";
import { saveRedirectUrl } from "../utils/redirectUrl";

// 登录 / 注册 / 找回密码 / 发验证码这几个表单请求（429 时由页面禁用按钮 N 秒，#897）
const AUTH_FORM_URLS = ["/auth/sigin", "/auth/sigup", "/auth/send/sms/code", "/auth/forgotpassword"];
export const isAuthFormRequest = (url?: string) => !!url && AUTH_FORM_URLS.some((prefix) => url.startsWith(prefix));

// 创建axios实例
const instance = axios.create({
    // 基本请求路径的抽取
    baseURL: window.location.origin,
    // 这个时间是每次请求的过期时间，请求超过十分钟会取消
    timeout: 600000,
});
// 请求拦截器
instance.interceptors.request.use(async (config) => {
    const data = await getPublicData();
    let token = "";
    const currentUrl = window?.location.pathname;
    if (
        ![
            "/f/website/userfile",
            "/f/website/privacy",
            "/f/website/register",
            "/f/website/login",
        ]?.includes(currentUrl)
    ) {
        // 使用tokenManager获取access_token(会自动刷新)
        token = await tokenManager.getAccessToken();
    }

    config.headers
        .set("X-Gsp-Auth", token)
        .set("Grpc-Metadata-GspUID", data["handleApp"]?.uid);

    if (
        config?.url?.includes("msgbox/") ||
        ["/domain/checkadmin"]?.includes(config?.url || "")
    ) {
        config.headers
            .set(
                "Grpc-Metadata-GspDomainID",
                data["handleApp"]?.currentDomainInfo?.id
            )
            .set(
                "Grpc-Metadata-GspPersonID",
                data["handleApp"]?.currentDomainInfo?.personUUID
            );
    }
    return config;
});

// 响应拦截器
instance.interceptors.response.use(
    (res) => {
        return res.data;
    },
    (err) => {
        // 网关限速 429 是纯文本、带 Retry-After（#897）
        // 后端自己回的 JSON 429（体带业务 code，如发码限频 code 3）不走这里：落到下面「data.code」那一支，
        // 把后端的错误体原样交给页面（页面按 code 3 等给原来的文案），不重试、不倒数（#898）
        if (isGatewayRateLimited(err)) {
            // 登录 / 注册 / 找回密码 / 发验证码：交给页面提示并把按钮禁用 N 秒，不重试
            if (isAuthFormRequest(err?.config?.url)) {
                const seconds = getRetryAfterSeconds(err.response);
                return Promise.reject({ rateLimited: true, retryAfter: seconds, error: rateLimitMessage(seconds) });
            }
            // 业务接口：短等待静默重试一次，否则「请求太频繁，请 N 秒后再试」
            return handleRateLimited(instance, err);
        }
        if (
            err?.response?.status == 403 &&
            err?.response?.data?.auth == "token invalid"
        ) {
            // 使用tokenManager清除token并跳转登录
            tokenManager.clearTokenData();
            sessionStorage.setItem("sessionStatus", "lapse");
            saveRedirectUrl();
            window.open(window.location.origin + "/f/website/login", "_self");
            return;
        } else if (
            err?.request?.responseURL?.includes("/auth/sigin") &&
            err?.response?.status == 401
        ) {
            // 等保登录错误处理
            return Promise.reject(err?.response?.data);
        } else if (
            err?.request?.responseURL?.includes("/auth/send/sms/code?mobile") &&
            err?.response?.status == 500
        ) {
            return Promise.reject(err?.response?.data);
        } else if (err?.response?.data?.code) {
            return Promise.reject(err?.response?.data);
        }
        return Promise.reject(
            err?.response?.data?.error ||
                err?.response?.data?.err ||
                err?.response?.data?.message ||
                err?.response?.data?.auth ||
                err?.response?.data?.exist ||
                err?.response?.data?.msg ||
                "请求错误"
        );
    }
);

export default instance;
